import React, { useCallback, useEffect, useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import Modal from "react-bootstrap/Modal";
import { ApexOptions } from "apexcharts";
import moment from "moment";
import Loading from "../LoadingPage/Loading";
import useRefrigerators from "../../Hooks/useRefrigerators";

interface IGraphModel {
  showModal: boolean;
  handleClose: () => void;
  modelName: string;
  sensorData: {
    id: string;
    inCentigrade?: boolean;
    refId?: string;
  } | null;
}

interface AlertLog {
  temp: number;
  timeStamp: string;
  alertType: string;
}

interface GraphData {
  timeStamp: string[];
  temp: number[];
  alerts?: boolean[];
  alertLogs: AlertLog[];
  tempHigh: number;
  tempLow: number;
}

interface TemperatureLog {
  timeStamp: string;
  temp: number;
  alert: boolean;
}

interface SensorLogsResponse {
  temperatureLogs: TemperatureLog[];
  alertLogs: AlertLog[];
  higherTempLimit: number;
  lowerTempLimit: number;
}

const FREQUENCY_OPTIONS = [
  { value: 1, label: "Last 24 Hours" },
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "Last 30 Days" },
] as const;

const initialGraphData: GraphData = {
  timeStamp: [],
  temp: [],
  alertLogs: [],
  tempHigh: 0,
  tempLow: 0,
};

const GraphModel: React.FC<IGraphModel> = ({
  showModal,
  handleClose,
  modelName,
  sensorData,
}) => {
  const [graphData, setGraphData] = useState<GraphData>(initialGraphData);
  const [selectedFrequency, setSelectedFrequency] = useState<number>(1);
  const { getRefrigeratorLogsAPI, getRefrigeratorLogsLoading } = useRefrigerators();

  useEffect(() => {
    if (showModal && sensorData?.refId) {
      fetchGraphData(selectedFrequency);
    }
  }, [showModal, sensorData?.refId, selectedFrequency]);

  const fetchGraphData = useCallback(
    async (frequency: number = 1) => {
      if (!sensorData?.refId) return;

      try {
        const response: SensorLogsResponse = await getRefrigeratorLogsAPI({
          id: sensorData.refId,
          ...(frequency > 1 ? { frequency } : {}),
        });

        if (response?.temperatureLogs) {
          const { temperatureLogs, alertLogs, higherTempLimit, lowerTempLimit } = response;

          setGraphData({
            timeStamp: temperatureLogs.map((log) => log?.timeStamp),
            temp: temperatureLogs.map((log) => log?.temp),
            alerts: temperatureLogs.map((log) => log?.alert),
            alertLogs: alertLogs || [],
            tempHigh: higherTempLimit || 0,
            tempLow: lowerTempLimit || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching sensor logs:", error);
        setGraphData(initialGraphData);
      }
    },
    [getRefrigeratorLogsAPI, sensorData?.refId]
  );

  const temperatureUnit = useMemo(
    () => (sensorData?.inCentigrade ? "°C" : "°F"),
    [sensorData?.inCentigrade]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        id: "temperature-chart",
        type: "line",
        zoom: { enabled: true },
        toolbar: {
          show: true,
          offsetY: -20,
          offsetX: -16,
          export: {
            csv: {
              headerCategory: "Date / Time",
              categoryFormatter(x: any) {
                return moment(x).format("MM/DD/YYYY hh:mm:ss A");
              },
            },
          },
          tools: {
            customIcons: [
              ...FREQUENCY_OPTIONS.map((item) => ({
                icon: `${item.value}D`,
                title: item.label,
                class: `frequency-selection ${selectedFrequency === item.value ? "selected" : ""}`,
                click: () => setSelectedFrequency(item.value),
              })),
              {
                icon: "",
                title: "",
                class: "icon-devider",
                click: () => { },
              },
            ],
          },
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      stroke: {
        curve: "smooth",
        width: [2, 2],
      },
      markers: {
        size: [0, 0],
        hover: { size: 6 },
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          format: "MM/dd HH:mm",
        },
        title: {
          text: "Date / Time",
          offsetY: 8,
          style: { fontSize: "14px", fontWeight: 600 },
        },
      },
      yaxis: {
        title: {
          text: `Temperature, ${temperatureUnit}`,
          offsetX: -5,
          style: { fontSize: "14px", fontWeight: 600 },
        },
        max: (() => {
          const maxTemp = graphData.temp.length > 0 ? Math.max(...graphData.temp) : 0;
          const maxAlertTemp = graphData.alertLogs.length > 0 ? Math.max(...graphData.alertLogs.map(a => a.temp)) : 0;
          const upperLimit = graphData.tempHigh || 0;
          return Math.max(maxTemp, maxAlertTemp, upperLimit) + 5;
        })(),
        min: (() => {
          const minTemp = graphData.temp.length > 0 ? Math.min(...graphData.temp) : 0;
          const minAlertTemp = graphData.alertLogs.length > 0 ? Math.min(...graphData.alertLogs.map(a => a.temp)) : 0;
          const lowerLimit = graphData.tempLow || 0;
          return Math.min(minTemp, minAlertTemp, lowerLimit) - 5;
        })(),
        labels: {
          formatter: (value: number) => `${value.toFixed(1)}`,
        },
      },
      annotations: {
        yaxis:
          graphData.tempHigh && graphData.tempLow
            ? [
              {
                y: graphData.tempHigh,
                borderColor: "#dc3545",
                strokeDashArray: 0,
                borderWidth: 2,
                label: {
                  position: "left",
                  style: {
                    color: "#dc3545",
                    fontSize: "12px",
                    fontWeight: "bold",
                  },
                  text: `${graphData.tempHigh}${temperatureUnit}`,
                },
              },
              {
                y: graphData.tempLow,
                borderColor: "#4444FF",
                strokeDashArray: 0,
                borderWidth: 2,
                label: {
                  position: "left",
                  style: {
                    color: "#4444FF",
                    fontSize: "12px",
                    fontWeight: "bold",
                  },
                  text: `${graphData.tempLow}${temperatureUnit}`,
                },
              },
            ]
            : [],
        points: graphData.alertLogs.map((alert) => ({
          x: new Date(alert.timeStamp).getTime(),
          y: alert.temp,
          marker: { size: 0 },
          customSVG: {
            SVG: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="14"><polygon points="8,0 16,14 0,14" fill="#dc3545" stroke="#fff" stroke-width="1.5"/><text x="8" y="12" text-anchor="middle" fill="#fff" font-size="10" font-weight="bold">!</text></svg>',
            cssClass: "",
            offsetX: -8,
            offsetY: -7,
          },
        })) || [],
      },
      legend: {
        show: false,
      },
      tooltip: {
        x: {
          format: "MM/dd/yy HH:mm:ss",
          show: false,
        },
        y: {
          formatter: (value: number | null) => value !== null ? `${value.toFixed(2)}${temperatureUnit}` : '',
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const value = series[seriesIndex][dataPointIndex];
          if (value === null || value === undefined) return '';
          return `<div class="apex-tooltip-custom ${graphData.alerts?.[dataPointIndex]}">${value.toFixed(2)}${temperatureUnit}</div>`;
        },
      },
      grid: {
        borderColor: "#e0e0e0",
        strokeDashArray: 3,
      },
      colors: ["#2E93fA", "#dc3545"],
    }),
    [graphData, selectedFrequency, temperatureUnit]
  );

  const chartSeries = useMemo(() => {
    if (graphData.timeStamp.length > 0) {
      const normalData: { x: number; y: number | null }[] = [];
      const alertData: { x: number; y: number | null }[] = [];

      graphData.timeStamp.forEach((ts, i) => {
        const x = new Date(ts).getTime();
        const y = graphData.temp[i] ?? null;
        const isAlert = graphData.alerts?.[i] === true;
        const prevIsAlert = i > 0 ? graphData.alerts?.[i - 1] === true : false;

        if (isAlert) {
          alertData.push({ x, y });
          // Include boundary point in normal series for line continuity
          if (!prevIsAlert && i > 0) {
            normalData.push({ x, y });
          } else {
            normalData.push({ x, y: null });
          }
        } else {
          normalData.push({ x, y });
          // Include boundary point in alert series for line continuity
          if (prevIsAlert) {
            alertData.push({ x, y });
          } else {
            alertData.push({ x, y: null });
          }
        }
      });

      return [
        {
          name: "Temperature",
          type: "line",
          data: normalData,
        },
        {
          name: "Out of Range",
          type: "line",
          data: alertData,
        },
      ];
    }
    /* Dummy series to force toolbar visibility */
    return [
      {
        name: "Temperature",
        type: "line",
        data: [{ x: new Date().getTime(), y: null }],
      },
      {
        name: "Out of Range",
        type: "line",
        data: [{ x: new Date().getTime(), y: null }],
      },
    ];
  }, [graphData, selectedFrequency]);

  if (!showModal) return null;

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{modelName}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "73vh", overflowY: "auto" }}>
        {getRefrigeratorLogsLoading && <Loading />}

        <div className="w-100 pt-4 d-flex flex-column align-items-center">
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={400}
            width={760}
          />
          {graphData.tempHigh && graphData.tempLow ? (
            <div className="d-flex justify-content-center mt-2 flex-wrap" style={{ gap: "1.5rem" }}>
              <div className="d-flex align-items-center gap-2">
                <span style={{ width: 20, height: 3, backgroundColor: "#dc3545", display: "inline-block" }} />
                <span style={{ fontSize: "13px", fontWeight: 600 }}>Max Limit ({graphData.tempHigh}{temperatureUnit})</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span style={{ width: 20, height: 3, backgroundColor: "#4444FF", display: "inline-block" }} />
                <span style={{ fontSize: "13px", fontWeight: 600 }}>Min Limit ({graphData.tempLow}{temperatureUnit})</span>
              </div>
              {graphData.alertLogs.length > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <svg width="12" height="10" style={{ display: "inline-block" }}>
                    <polygon points="6,0 12,10 0,10" fill="#dc3545" />
                  </svg>
                  <span style={{ fontSize: "13px", fontWeight: 600 }}>Alert Logs</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GraphModel;
